import { type QueryObserverResult } from '@tanstack/react-query'
import { LoaderCircleIcon } from 'lucide-react'
import { createContext, useContext, useMemo } from 'react'
import { Outlet } from 'react-router'

import { Banner } from 'slab'
import { getYearFromDateString } from 'utils'
import { useGetQuotingProspectsProspectIdQuotes } from 'utils/web_api'
import { type QuotingQuote } from 'utils/web_api.schemas'

import { useEmployer } from './EmployerProvider'

type QuoteContextType = {
  quote: QuotingQuote | null;
  loading: boolean;
  refetch: ReturnType<typeof useGetQuotingProspectsProspectIdQuotes>['refetch'];
  shouldShowPendingPricesWarning: boolean;
  quoteYear: number | null;
}

export const QuoteContext = createContext<QuoteContextType>({
  quote: null,
  loading: false,
  refetch: async () => ({ data: null, isLoading: false, isError: false, error: null } as QueryObserverResult<unknown, unknown>),
  shouldShowPendingPricesWarning: false,
  quoteYear: null,
})

export function useQuote () {
  return useContext(QuoteContext)
}

export function QuoteProvider () {
  const { employer } = useEmployer()

  const getMostRecentQuote = (quotesData: QuotingQuote[]) => {
    if (!quotesData || quotesData.length === 0) return null
    const sortedQuotes = [...quotesData].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
    return sortedQuotes[0]
  }

  const {
    data: quotes,
    isLoading,
    error,
    refetch
  } = useGetQuotingProspectsProspectIdQuotes(
    employer?.quoting_prospect_id ?? '',
    undefined,
    {
      query: {
        enabled: !!employer?.quoting_prospect_id,
      },
    }
  )

  const currentQuote: QuotingQuote | null = useMemo(() => {
    if (!quotes?.data) return null
    return getMostRecentQuote(quotes.data)
  }, [quotes])

  const quoteYear = useMemo(() => {
    if (!currentQuote?.coverage_start_date) return null
    return getYearFromDateString(currentQuote.coverage_start_date)
  }, [currentQuote?.coverage_start_date])

  // This is only used to determine if we should show the PendingRatesPage
  // For determining if quote data is preliminary, use quote.preliminary instead
  const shouldShowPendingPricesWarning = useMemo(() => {
    if (!currentQuote?.coverage_start_date) return false

    const coverageStartDate = new Date(currentQuote.coverage_start_date + 'T00:00:00')
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth() // 0-indexed
    const currentDate = new Date().getDate()

    const isNextYear = coverageStartDate.getFullYear() === currentYear + 1
    const isBeforeNovember = currentMonth < 10 || (currentMonth === 10 && currentDate < 1)

    return isNextYear && isBeforeNovember
  }, [currentQuote?.coverage_start_date])

  const value = useMemo(
    () => ({ quote: currentQuote, loading: isLoading, refetch, shouldShowPendingPricesWarning, quoteYear }),
    [currentQuote, isLoading, refetch, shouldShowPendingPricesWarning, quoteYear]
  )

  if (isLoading) {
    return (
      <div className='flex grow items-center justify-center'>
        <LoaderCircleIcon size={24} className='animate-spin text-gray-500' />
      </div>
    )
  }

  if (error) {
    return (
      <div className='mt-20 max-w-[45rem] mx-auto'>
        <Banner type='critical' title='Error loading quote'>
          {error instanceof Error && error.message}
        </Banner>
      </div>
    )
  }

  return (
    <QuoteContext.Provider value={value}>
      <Outlet />
    </QuoteContext.Provider>
  )
}
